import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import * as api from './xtp.api';
import {
  GET_XTPS,
  GetXtpAction,
  GO_TO_XTP_CREATE,
  GO_TO_XTP_EDIT,
  GO_TO_XTP_VIEW,
  DELETE_XTP,
  CREATE_XTP,
  UPDATE_XTP,
  UPDATE_ERRORS,
  GET_XTP_OPTIONS,
  UPDATE_XTP_OPTIONS,
  Xtp,
  XtpOptions,
  CreateXtpThunkAction,
  UpdateXtpThunkAction,
  DeleteXtpThunkAction,
  GetXtpOptionsAction,
} from './xtp.types';
import { XTP, NETWORK, SYSTEM_CONFIGURATION_ROUTE } from '../../constants';
import { extractErrorMessage } from '../../utils';
import * as xtpSelectors from './xtp.selectors';

export function updteXtpErrors(errors: { xtp: string, xtpOptions: string}): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: UPDATE_ERRORS,
      payload: errors
    });
  };
}

export function goToXtpCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XTP_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP}`);
  };
}

export function goToXtpEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XTP_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP}/edit`);
  };
}

export function goToXtpView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XTP_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP}/view`);
  };
}

export function getXtps(deliveryFunctionId: string): GetXtpAction {
  return {
    type: GET_XTPS,
    payload: api.getXtps(deliveryFunctionId),
  };
}

export function createXtp(
  deliveryFunctionId: string,
  xtpBody: WithAuditDetails<Xtp>,
  xtpOptionsBody: WithAuditDetails<XtpOptions>,
  history: History,
): CreateXtpThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    const errors = { xtp: '', xtpOptions: '' };
    let errorMessage;

    try {
      await dispatch({
        type: CREATE_XTP,
        payload: api.createXtp(deliveryFunctionId, xtpBody),
      });
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      errors.xtp = i18n.t('xtp.create.failed', { name: xtpBody.data.lbId, error: errorMessage });
    }

    try {
      await dispatch({
        type: UPDATE_XTP_OPTIONS,
        payload: api.updateXtpOptions(deliveryFunctionId, xtpOptionsBody.data.id, xtpOptionsBody),
      });
    } catch (e) {
      errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      errors.xtpOptions = i18n.t('xtp.create.failed', { name: xtpOptionsBody.data.id, error: errorMessage });
    }

    if (errors.xtp === '' && errors.xtpOptions === '') {
      showSuccessMessage(i18n.t('xtp.create.success', { name: xtpBody.data.lbId }));
    }

    if (errors.xtp !== '' && errors.xtpOptions !== '') {
      showError(i18n.t('xtp.create.failed', { name: xtpOptionsBody.data.id, error: errorMessage }));
    } else {
      dispatch(goToXtpView(history));
    }

    dispatch(updteXtpErrors(errors));

  };
}

export function updateXtp(
  deliveryFunctionId: string,
  xtpBody: WithAuditDetails<Xtp>,
  xtpOptionsBody: WithAuditDetails<XtpOptions>,
  history: History,
): UpdateXtpThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    const errors = { xtp: '', xtpOptions: '' };

    try {
      await dispatch({
        type: UPDATE_XTP,
        payload: api.updateXtp(deliveryFunctionId, xtpBody.data.id, xtpBody),
      });
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      errors.xtp = i18n.t('xtp.edit.failed', { name: xtpBody.data.lbId, error: errorMessage });
    }

    try {
      await dispatch({
        type: UPDATE_XTP_OPTIONS,
        payload: api.updateXtpOptions(deliveryFunctionId, xtpOptionsBody.data.id, xtpOptionsBody),
      });
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      errors.xtpOptions = i18n.t('xtp.edit.failed', { name: xtpOptionsBody.data.id, error: errorMessage });
    }

    if (errors.xtp === '' && errors.xtpOptions === '') {
      showSuccessMessage(i18n.t('xtp.edit.success', { name: xtpBody.data.lbId }));
    }

    if (errors.xtp !== '' && errors.xtpOptions !== '') {
      const error = errors.xtp ?? errors.xtpOptions;
      showError(i18n.t('xtp.edit.failed', { error }));
    } else {
      dispatch(goToXtpView(history));
    }

    dispatch(updteXtpErrors(errors));
  };
}

export function deleteXtp(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteXtpThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedXtp] = xtpSelectors.getXtps(getState());

    try {
      await dispatch({
        type: DELETE_XTP,
        payload: api.deleteXtp(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('xtp.delete.success', { name: selectedXtp?.lbId }), 5000);
      if (history != null) {
        dispatch(goToXtpView(history));
      }
    } catch (e) {
      showError(i18n.t('xtp.delete.failed', { name: selectedXtp?.lbId }));

    } finally {
      dispatch(getXtps(deliveryFunctionId));
    }
  };
}

export function getXtpOptions(deliveryFunctionId: string): GetXtpOptionsAction {
  return {
    type: GET_XTP_OPTIONS,
    payload: api.getXtpOptions(deliveryFunctionId),
  };
}
